/**
 * Custom Domain Settings component for the Settings page.
 * Allows users to add, verify, and remove custom domains for white-labeling.
 */

import { useState, useEffect } from 'react';
import { useDomainsStore } from '@/features/domains/stores/domains.store';
import { logger } from '@/features/help/utils/logger';
import { toast } from 'react-hot-toast';
import {
  buildCustomDomainHostname,
  isValidRootDomain,
  isValidSubdomainLabel,
  normalizeHostnamePart,
} from '@/features/domains/utils/custom-domain.util';
import {
  Globe,
  Plus,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Trash2,
  RefreshCw,
  Copy,
  HelpCircle,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/features/ui/primitives/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/features/ui/primitives/dialog';

interface DnsRecord {
  type: string;
  name: string;
  value: string;
  purpose?: string;
}

/**
 * DNS Record display component with tooltips explaining alternative field names
 */
const DnsRecordRow = ({ record }: { record: DnsRecord }) => {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="flex items-center justify-between bg-gray-50 p-3 rounded border border-gray-200">
      <div className="flex-1 grid grid-cols-3 gap-4 text-sm">
        <div>
          <span className="text-gray-500 text-xs flex items-center gap-1">
            Type
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-3 w-3 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Also called: Record Type</p>
              </TooltipContent>
            </Tooltip>
          </span>
          <span className="font-mono font-medium">{record.type}</span>
        </div>
        <div>
          <span className="text-gray-500 text-xs flex items-center gap-1">
            Name
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-3 w-3 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Also called: Host, Hostname, Subdomain</p>
              </TooltipContent>
            </Tooltip>
          </span>
          <span className="font-mono">{record.name}</span>
        </div>
        <div>
          <span className="text-gray-500 text-xs flex items-center gap-1">
            Value
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-3 w-3 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Also called: Points To, Target, Alias Data, Content</p>
              </TooltipContent>
            </Tooltip>
          </span>
          <span className="font-mono text-xs break-all">{record.value}</span>
        </div>
      </div>
      <button
        onClick={() => copyToClipboard(record.value)}
        className="ml-2 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
        title="Copy value"
      >
        <Copy className="h-4 w-4" />
      </button>
    </div>
  );
};

/**
 * Status badge component for domain verification status
 */
const StatusBadge = ({ status }: { status: string }) => {
  const config = {
    verified: {
      icon: CheckCircle,
      text: 'Verified',
      className: 'bg-green-100 text-green-800',
    },
    pending: {
      icon: AlertCircle,
      text: 'Pending',
      className: 'bg-yellow-100 text-yellow-800',
    },
    misconfigured: {
      icon: XCircle,
      text: 'Misconfigured',
      className: 'bg-red-100 text-red-800',
    },
    failed: {
      icon: XCircle,
      text: 'Failed',
      className: 'bg-red-100 text-red-800',
    },
    not_present_on_vercel: {
      icon: XCircle,
      text: 'Not on Vercel',
      className: 'bg-red-100 text-red-800',
    },
  };

  const { icon: Icon, text, className } = config[status as keyof typeof config] || config.pending;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${className}`}>
      <Icon className="h-3 w-3" />
      {text}
    </span>
  );
};

export const CustomDomainSettings = () => {
  const {
    domains,
    isLoading,
    isAdding,
    isVerifying,
    isRemoving,
    error,
    dnsRecords,
    fetchDomains,
    addDomain,
    verifyDomain,
    removeDomain,
    clearError,
  } = useDomainsStore();

  const [newDomain, setNewDomain] = useState(''); // root domain (e.g., "yourcompany.com")
  const [newSubdomain, setNewSubdomain] = useState('apply'); // subdomain label (e.g., "apply", "app", "fund")
  const [showAddForm, setShowAddForm] = useState(false);
  // Tracks which domain IDs have their DNS config collapsed (empty Set = all expanded by default)
  const [collapsedDnsRecords, setCollapsedDnsRecords] = useState<Set<string>>(new Set());
  const [localDnsRecords, setLocalDnsRecords] = useState<DnsRecord[]>([]);
  // State for remove confirmation dialog
  const [domainToRemove, setDomainToRemove] = useState<{ id: string; domain: string } | null>(null);

  // Fetch domains on mount if not already cached
  useEffect(() => {
    if (domains.length === 0) {
      fetchDomains();
    }
  }, [domains.length, fetchDomains]);

  // Update local DNS records when store changes
  useEffect(() => {
    if (dnsRecords.length > 0) {
      setLocalDnsRecords(dnsRecords);
    }
  }, [dnsRecords]);

  const handleAddDomain = async () => {
    if (!newSubdomain.trim() || !newDomain.trim()) {
      toast.error('Please enter a subdomain and a root domain');
      return;
    }

    const cleanRootDomain = normalizeHostnamePart(newDomain);
    const cleanSubdomain = normalizeHostnamePart(newSubdomain);

    // Help users avoid pasting a full hostname into the root domain field
    if (cleanRootDomain.split('.').filter(Boolean).length >= 3) {
      toast.error('Please enter only the root domain (e.g., yourcompany.com)');
      return;
    }

    if (!isValidSubdomainLabel(cleanSubdomain)) {
      toast.error('Please enter a valid subdomain (letters, numbers, hyphens)');
      return;
    }

    if (!isValidRootDomain(cleanRootDomain)) {
      toast.error('Please enter a valid root domain (e.g., yourcompany.com)');
      return;
    }

    const fullDomain = buildCustomDomainHostname(cleanSubdomain, cleanRootDomain);
    
    logger.info('CustomDomainSettings', 'Adding domain', {
      rootDomain: cleanRootDomain,
      subdomain: cleanSubdomain,
      fullDomain,
    });

    const result = await addDomain(fullDomain);

    if (result.success) {
      toast.success('Domain added! Configure the DNS records below.');
      setNewDomain('');
      setNewSubdomain('apply');
      setShowAddForm(false);
      if (result.dnsRecords) {
        setLocalDnsRecords(result.dnsRecords);
        // DNS records are shown by default, no need to explicitly expand
      }
    } else {
      toast.error(error || 'Failed to add domain');
    }
  };

  const handleVerifyDomain = async (domainId: string) => {
    logger.info('CustomDomainSettings', 'Verifying domain', { domainId });

    const result = await verifyDomain(domainId);

    if (result.success) {
      if (result.verified) {
        toast.success('Domain verified successfully! Your custom domain is now active.');
        // Remove from collapsed set if it was there (cleanup)
        setCollapsedDnsRecords((prev) => {
          const next = new Set(prev);
          next.delete(domainId);
          return next;
        });
      } else {
        // DNS not yet configured - ensure it's expanded (remove from collapsed set)
        if (result.dnsRecords) {
          setLocalDnsRecords(result.dnsRecords);
          setCollapsedDnsRecords((prev) => {
            const next = new Set(prev);
            next.delete(domainId);
            return next;
          });
        }
      }
    } else {
      toast.error(error || 'Verification failed');
    }
  };

  /**
   * Opens the confirmation dialog for domain removal
   */
  const openRemoveConfirmation = (domainId: string, domain: string) => {
    setDomainToRemove({ id: domainId, domain });
  };

  /**
   * Handles the actual domain removal after user confirms
   */
  const handleRemoveDomain = async () => {
    if (!domainToRemove) return;

    const { id: domainId, domain } = domainToRemove;
    logger.info('CustomDomainSettings', 'Removing domain', { domainId, domain });

    const success = await removeDomain(domainId);

    if (success) {
      toast.success('Domain removed successfully');
      // Cleanup: remove from collapsed set if it was there
      setCollapsedDnsRecords((prev) => {
        const next = new Set(prev);
        next.delete(domainId);
        return next;
      });
    } else {
      toast.error(error || 'Failed to remove domain');
    }

    // Close the dialog
    setDomainToRemove(null);
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-brand-600" />
            <h2 className="text-xl font-semibold text-gray-900">Custom Domain</h2>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-sm">
                  Add your company domain to white-label your application forms. Your forms will be
                  available at <span className="font-medium">your-subdomain.yourdomain.com</span>.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          {!showAddForm && domains.length === 0 && (
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded-md transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Domain
            </button>
          )}
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <p>{error}</p>
              <button onClick={clearError} className="text-red-600 underline text-xs mt-1">
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Loading state */}
        {isLoading && domains.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-500">Loading domains...</span>
          </div>
        )}

        {/* Add domain form */}
        {showAddForm && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Add Custom Domain</h3>
            <div className="flex gap-2 items-center">
              <div className="flex flex-1">
                <input
                  type="text"
                  value={newSubdomain}
                  onChange={(e) => setNewSubdomain(e.target.value)}
                  placeholder="apply"
                  className="w-28 px-3 py-2 border border-r-0 border-gray-300 rounded-l-md text-sm focus:ring-brand-500 focus:border-brand-500"
                  disabled={isAdding}
                  aria-label="Subdomain"
                />
                <span className="text-sm font-medium text-gray-700 bg-gray-100 px-2 py-2 border-y border-gray-300">
                  .
                </span>
                <input
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="yourcompany.com"
                  className="flex-1 px-3 py-2 border border-l-0 border-gray-300 rounded-r-md text-sm focus:ring-brand-500 focus:border-brand-500"
                  disabled={isAdding}
                  aria-label="Root domain"
                />
              </div>
              <button
                onClick={handleAddDomain}
                disabled={isAdding || !newSubdomain.trim() || !newDomain.trim()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isAdding ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add'
                )}
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewDomain('');
                  setNewSubdomain('apply');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                disabled={isAdding}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Domains list */}
        {!isLoading && domains.length === 0 && !showAddForm && (
          <div className="text-center py-8">
            <Globe className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">No custom domain configured</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center gap-1 px-4 py-2 bg-brand-600 text-white rounded-md text-sm font-medium hover:bg-brand-700"
            >
              <Plus className="h-4 w-4" />
              Add Your First Domain
            </button>
          </div>
        )}

        {domains.length > 0 && (
          <div className="space-y-4">
            {domains.map((domain) => (
              <div key={domain.id} className="border border-gray-200 rounded-lg">
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Globe className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">{domain.domain}</p>
                      <p className="text-xs text-gray-500">
                        Added {new Date(domain.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={domain.verification_status} />
                    {domain.verification_status !== 'verified' &&
                      domain.verification_status !== 'not_present_on_vercel' && (
                      <button
                        onClick={() => handleVerifyDomain(domain.id)}
                        disabled={isVerifying}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded-md transition-colors disabled:opacity-50"
                      >
                        {isVerifying ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        Verify
                      </button>
                    )}
                    <button
                      onClick={() => openRemoveConfirmation(domain.id, domain.domain)}
                      disabled={isRemoving}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                      title="Remove domain"
                    >
                      {isRemoving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* DNS Records section */}
                {domain.verification_status !== 'verified' &&
                  domain.verification_status !== 'not_present_on_vercel' && (
                  <div className="border-t border-gray-200">
                    <button
                      onClick={() =>
                        setCollapsedDnsRecords((prev) => {
                          const next = new Set(prev);
                          if (next.has(domain.id)) {
                            next.delete(domain.id);
                          } else {
                            next.add(domain.id);
                          }
                          return next;
                        })
                      }
                      className="w-full px-4 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 flex items-center justify-between"
                    >
                      <span>DNS Configuration</span>
                      <span className="text-xs text-gray-400">
                        {collapsedDnsRecords.has(domain.id) ? 'Show' : 'Hide'}
                      </span>
                    </button>

                    {!collapsedDnsRecords.has(domain.id) && (
                      <div className="px-4 pb-4 space-y-3">
                        <p className="text-sm text-gray-600">
                          Add the following DNS records to your domain provider:
                        </p>

                        {localDnsRecords.length > 0 ? (
                          <div className="space-y-2">
                            {localDnsRecords.map((record, index) => (
                              <DnsRecordRow key={index} record={record} />
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {/* Default CNAME record */}
                            <DnsRecordRow
                              record={{
                                type: 'CNAME',
                                name: domain.domain.split('.')[0],
                                value: 'cname.vercel-dns.com',
                              }}
                            />
                          </div>
                        )}

                        <div className="mt-4 p-3 bg-brand-50 border border-brand-200 rounded-md">
                          <p className="text-sm text-brand-800">
                            <strong>Note:</strong> DNS changes can take up to 48 hours to propagate.
                            Click "Verify" once you've added the records.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {domain.verification_status === 'not_present_on_vercel' && (
                  <div className="border-t border-gray-200 px-4 py-3 bg-red-50">
                    <p className="text-sm text-red-700">
                      This domain is no longer attached to the Vercel project. Remove it here and
                      add it again to reconnect.
                    </p>
                  </div>
                )}

                {/* Verified domain info */}
                {domain.verification_status === 'verified' && domain.verified_at && (
                  <div className="border-t border-gray-200 px-4 py-3 bg-green-50">
                    <p className="text-sm text-green-700">
                      ✓ Domain verified on {new Date(domain.verified_at).toLocaleDateString()}.
                      Your applicants can now access forms at{' '}
                      <a
                        href={`https://${domain.domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium underline"
                      >
                        {domain.domain}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            ))}

            {/* Add another domain button */}
            {!showAddForm && (
              <button
                onClick={() => setShowAddForm(true)}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-brand-500 hover:text-brand-600 transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Another Domain
              </button>
            )}
          </div>
        )}
      </div>

      {/* Remove Domain Confirmation Dialog */}
      <Dialog open={!!domainToRemove} onOpenChange={(open) => !open && setDomainToRemove(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove Domain</DialogTitle>
            <DialogDescription className="text-gray-700">
              Are you sure you want to remove <span className="font-medium">{domainToRemove?.domain}</span>? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              onClick={() => setDomainToRemove(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleRemoveDomain}
              disabled={isRemoving}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {isRemoving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove Domain'
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

